import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronsLeftRight, SlidersHorizontal, X } from 'lucide-react';
import { tr } from '../lib/i18n.ts';
import { openSettings } from '../settings/store.ts';
import { INFO_WIDGETS } from './registry.ts';
import { useInfoBarPrefs, usePrefsStore } from './prefsStore.ts';
import { useWeather } from './queries.ts';
import { describeCondition } from './weather.ts';
import { ConditionIconView } from './widgets/WeatherWidget.tsx';
import { ClockTime } from './widgets/ClockWidget.tsx';
import { BellWidget } from './widgets/BellWidget.tsx';

/** Phone version: temperature, time and bell on one line; tapping it opens the full bar as a sheet. */
export function CompactInfoBar() {
  const prefs = useInfoBarPrefs();
  const [expanded, setExpanded] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  if (!prefs.visible) {
    return (
      <section className="infobar-compact infobar-compact-collapsed" aria-label={tr.infobar.label}>
        <button
          type="button"
          className="infobar-button"
          aria-label={tr.infobar.show}
          onClick={() => usePrefsStore.getState().update({ visible: true })}
        >
          <ChevronsLeftRight size={17} aria-hidden />
        </button>
      </section>
    );
  }

  return (
    <section className="infobar-compact" aria-label={tr.infobar.label}>
      <button
        ref={opener}
        type="button"
        className="infobar-compact-main"
        aria-haspopup="dialog"
        aria-expanded={expanded}
        aria-label={tr.infobar.expand}
        onClick={() => setExpanded(true)}
      >
        {prefs.widgets.weather && <CompactWeather />}
        <ClockTime />
        <ChevronDown size={14} aria-hidden />
      </button>
      {prefs.widgets.bell && <BellWidget variant="bar" />}
      {expanded && (
        <Sheet
          onClose={() => {
            setExpanded(false);
            opener.current?.focus();
          }}
        />
      )}
    </section>
  );
}

function CompactWeather() {
  const { locations } = useInfoBarPrefs();
  const { data } = useWeather(locations[0]!, true);
  if (!data) return <span className="iw-loading">{tr.infobar.loadingShort}</span>;
  return (
    <span className="iw-now" title={describeCondition(data.current.weatherCode, data.current.isDay).label}>
      <ConditionIconView code={data.current.weatherCode} isDay={data.current.isDay} />
      <span className="iw-temp">{Math.round(data.current.temperature)}°</span>
    </span>
  );
}

function Sheet({ onClose }: { onClose: () => void }) {
  const prefs = useInfoBarPrefs();
  const ref = useRef<HTMLDivElement>(null);
  // Read through a ref so a re-render never re-runs the effect and pulls focus out of the search field.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    ref.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="infobar-scrim" onPointerDown={(event) => event.target === event.currentTarget && onClose()}>
      <div ref={ref} className="infobar-sheet" role="dialog" aria-modal="true" aria-label={tr.infobar.label} tabIndex={-1}>
        <button type="button" className="infobar-sheet-close" aria-label={tr.infobar.close} onClick={onClose}>
          <X size={18} aria-hidden />
        </button>
        {INFO_WIDGETS.filter((widget) => widget.zone === 'main' && prefs.widgets[widget.id] !== false).map(({ id, Bar }) => (
          <Bar key={id} variant="sheet" />
        ))}
        <button
          type="button"
          className="btn"
          onClick={() => {
            onClose();
            openSettings();
          }}
        >
          <SlidersHorizontal size={15} aria-hidden />
          {tr.settings.open}
        </button>
      </div>
    </div>
  );
}
